import React from "react";
import {
    Button,
    Card,
    Descriptions,
    Divider,
    Space,
    Typography,
    message,
} from "antd";
import {
    DownloadOutlined,
    EyeOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import {
    downloadContractPdf,
    downloadInvoicePdf,
    downloadGeneratedAllocationSlipPdf,
    downloadGeneratedAllocationSampleFilledPdf,
    downloadScannedLatestSlipPdf,
    downloadScannedLatestSampleFilledPdf,
} from "../../api/customerTransactionApi";
import RequestStatusTag from "./RequestStatusTag";
import dayjs from "dayjs";

const { Text } = Typography;

function formatDateTime(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString();
}

function formatAmount(value) {
    if (value === null || value === undefined || value === "") return "-";
    const number = Number(value);
    if (!Number.isFinite(number)) return "-";

    return number.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function saveBlob(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

function openBlob(blob) {
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

async function getFileResponse(loader, fallbackName, entity) {
    const { blob, headers } = await loader(entity.id);
    return {
        blob,
        fileName:
            headers.get("x-filename") ||
            headers.get("content-disposition")?.match(/filename=\"?([^\";]+)\"?/)?.[1] ||
            fallbackName,
    };
}

function FileActionCard({ title, fileName, onView, onDownload }) {
    return (
        <Card size="small">
            <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                <Space align="start">
                    <FilePdfOutlined style={{ fontSize: 18, marginTop: 2 }} />
                    <Space direction="vertical" size={0}>
                        <Text strong>{title}</Text>
                        <Text type="secondary" style={{ fontSize: 12, wordBreak: "break-all" }}>
                            {fileName || "No file uploaded"}
                        </Text>
                    </Space>
                </Space>

                <Space>
                    <Button icon={<EyeOutlined />} onClick={onView} disabled={!fileName}>
                        View
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={onDownload} disabled={!fileName}>
                        Download
                    </Button>
                </Space>
            </Space>
        </Card>
    );
}

async function handleAllocationPdf(loader, allocation, fallbackName, mode) {
    const { blob, headers } = await loader(allocation.id);

    const fileName =
        headers.get("x-filename") ||
        headers.get("content-disposition")?.match(/filename=\"?([^\";]+)\"?/)?.[1] ||
        fallbackName;

    if (mode === "view") {
        openBlob(blob);
        return;
    }

    saveBlob(blob, fileName);
}

export default function CustomerRequestDetail({ data }) {
    const handleInvoice = async (mode = "download") => {
        try {
            const { blob, fileName } = await getFileResponse(
                downloadInvoicePdf,
                data.invoiceFileName || "invoice.pdf",
                data
            );

            if (mode === "view") {
                openBlob(blob);
                return;
            }

            saveBlob(blob, fileName);
        } catch (error) {
            message.error(error.message || "Failed to open invoice");
        }
    };

    const handleContract = async (mode = "download") => {
        try {
            const { blob, fileName } = await getFileResponse(
                downloadContractPdf,
                data.contractFileName || "contract.pdf",
                data
            );

            if (mode === "view") {
                openBlob(blob);
                return;
            }

            saveBlob(blob, fileName);
        } catch (error) {
            message.error(error.message || "Failed to open contract");
        }
    };

    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title={`Request Detail #${data?.id || ""}`}>
                <Descriptions bordered column={{ xs: 1, md: 2 }} size="middle">
                    <Descriptions.Item label="Request Number">
                        {data?.requestNumber || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Institution">
                        {data?.institutionName || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Registration Datetime">
                        {formatDateTime(data?.registrationDateTime)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Request Datetime">
                        {formatDateTime(data?.requestDateTime)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Amount">
                        {formatAmount(data?.paymentAmount)} {data?.currency || ""}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <RequestStatusTag status={data?.customerSubmissionStatus} />
                    </Descriptions.Item>

                    <Descriptions.Item label="Beneficiary Company">
                        {data?.beneficiaryCompany || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Beneficiary Bank">
                        {data?.beneficiaryBank || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Beneficiary Account" span={2}>
                        {data?.beneficiaryAccount || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Payment Purpose" span={2}>
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {data?.paymentPurpose || "-"}
                        </div>
                    </Descriptions.Item>

                    <Descriptions.Item label="Created At" span={2}>
                        {formatDateTime(data?.createdAt)}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Attachments">
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <FileActionCard
                        title="Invoice PDF"
                        fileName={data?.invoiceFileName}
                        onView={() => handleInvoice("view")}
                        onDownload={() => handleInvoice("download")}
                    />
                    <FileActionCard
                        title="Contract PDF"
                        fileName={data?.contractFileName}
                        onView={() => handleContract("view")}
                        onDownload={() => handleContract("download")}
                    />
                </Space>
            </Card>

            <Card title="Allocation PDFs">
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {(data?.allocations || []).length === 0 ? (
                        <Text type="secondary">No agreed allocations yet.</Text>
                    ) : (
                        data.allocations.map((allocation) => (
                            <Card
                                key={allocation.id}
                                size="small"
                                title={`Allocation #${allocation.id} / ${formatAmount(allocation.allocatedAmount)} ${data?.currency || ""}`}
                            >
                                <Space direction="vertical" size={10} style={{ width: "100%" }}>

                                    <FileActionCard
                                        title="Scanned Latest Slip PDF"
                                        fileName={allocation.scannedLatestSlipPdfFileName}
                                        onView={() =>
                                            handleAllocationPdf(
                                                downloadScannedLatestSlipPdf,
                                                allocation,
                                                `allocation-${allocation.id}-scanned-slip.pdf`,
                                                "view"
                                            )
                                        }
                                        onDownload={() =>
                                            handleAllocationPdf(
                                                downloadScannedLatestSlipPdf,
                                                allocation,
                                                `allocation-${allocation.id}-scanned-slip.pdf`,
                                                "download"
                                            )
                                        }
                                    />

                                    <FileActionCard
                                        title="Scanned Latest Sample Filled PDF"
                                        fileName={allocation.scannedLatestSampleFilledPdfFileName}
                                        onView={() =>
                                            handleAllocationPdf(
                                                downloadScannedLatestSampleFilledPdf,
                                                allocation,
                                                `allocation-${allocation.id}-scanned-sample-filled.pdf`,
                                                "view"
                                            )
                                        }
                                        onDownload={() =>
                                            handleAllocationPdf(
                                                downloadScannedLatestSampleFilledPdf,
                                                allocation,
                                                `allocation-${allocation.id}-scanned-sample-filled.pdf`,
                                                "download"
                                            )
                                        }
                                    />
                                </Space>
                                {allocation.approvedAt ? (
                                    <Text type="success">
                                        Approved: {dayjs(allocation.approvedAt).format("YYYY-MM-DD HH:mm:ss")}
                                    </Text>
                                ) : (
                                    <Text type="secondary">Pending approval</Text>
                                )}
                            </Card>
                        ))
                    )}
                </Space>
            </Card>
        </Space>
    );
}
